import React, { useState } from "react";
import { useAuth } from "wasp/client/auth";
import { useQuery } from "wasp/client/operations";
import { getCarpools } from "wasp/client/operations";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Plus,
  Car,
  MapPin,
  Clock,
  Calendar,
  Users,
  Search,
  Filter,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  EnhancedActionButton,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";

export default function CarpoolPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"ALL" | "DRIVER" | "PASSENGER">(
    "ALL",
  );
  const { isMobile, isFilterExpanded, setIsFilterExpanded } = useResponsiveLayout();

  const {
    data: carpools,
    isLoading,
    error,
  } = useQuery(
    getCarpools,
    {
      locationId: selectedLocation?.id || "",
      filters: activeTab !== "ALL" ? { role: activeTab } : {},
    },
    {
      enabled: !!selectedLocation?.id,
    },
  );

  const handleCreateNew = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/carpooling/add`);
    } else {
      navigate("/carpooling/add");
    }
  };

  return (
    <DashboardLayout>
      <div className="w-full h-full flex flex-col">
        {/* Fixed Main Header */}
        <div className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Carpooling</h1>
                <p className="text-gray-500 text-sm mt-1">
                  Find or offer a ride ({carpools?.length || 0})
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  variant={isFilterExpanded ? "primary" : "secondary"}
                  className="hidden lg:flex"
                />

                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-4">
                        <label className="text-sm font-medium">I am looking for...</label>
                        <div className="flex flex-col gap-2">
                          <Button
                            variant={activeTab === "ALL" ? "default" : "outline"}
                            onClick={() => { setActiveTab("ALL"); setIsFilterExpanded(false); }}
                            className="justify-start px-4 h-12"
                          >
                            All Rides
                          </Button>
                          <Button
                            variant={activeTab === "DRIVER" ? "default" : "outline"}
                            onClick={() => { setActiveTab("DRIVER"); setIsFilterExpanded(false); }}
                            className="justify-start px-4 h-12"
                          >
                            Passengers (I'm Driving)
                          </Button>
                          <Button
                            variant={activeTab === "PASSENGER" ? "default" : "outline"}
                            onClick={() => { setActiveTab("PASSENGER"); setIsFilterExpanded(false); }}
                            className="justify-start px-4 h-12"
                          >
                            Drivers (I need a ride)
                          </Button>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user && (
                  <EnhancedActionButton
                    onClick={handleCreateNew}
                    icon={Plus}
                    label="Post Ride"
                    hideLabelOnMobile
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Filters (Desktop) */}
        <div
          className={cn(
            "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300 ease-in-out hidden lg:block",
            isFilterExpanded ? "py-4 max-h-[100px] opacity-100" : "py-0 max-h-0 opacity-0 border-none",
          )}
        >
          <div className="px-6 flex items-center gap-4">
            <button
              onClick={() => setActiveTab("ALL")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === "ALL"
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
            >
              All Rides
            </button>
            <button
              onClick={() => setActiveTab("DRIVER")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === "DRIVER"
                  ? "bg-blue-600 text-white"
                  : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                }`}
            >
              Looking for Passengers
            </button>
            <button
              onClick={() => setActiveTab("PASSENGER")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === "PASSENGER"
                  ? "bg-green-600 text-white"
                  : "bg-green-50 text-green-600 hover:bg-green-100"
                }`}
            >
              Looking for Drivers
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={cn(
          "flex-1 overflow-y-auto transition-[padding] duration-300",
          isFilterExpanded ? "pt-[calc(var(--header-height)+9.5rem)]" : "pt-[calc(var(--header-height)+5rem)]"
        )}>
          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-8">
              Failed to load carpools. Please try again.
            </div>
          ) : carpools?.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Car className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Rides Found
                </h3>
                <p className="text-gray-600">
                  {activeTab !== "ALL"
                    ? "Try adjusting your filters to find what you're looking for."
                    : "Be the first to post a ride offer or request!"}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {carpools.map((item: any) => {
                const metadata = (item.metadata as any) || {};
                const isDriver = metadata.role === "DRIVER";

                return (
                  <Link
                    key={item.id}
                    to={
                      selectedLocation?.locationCode
                        ? `/locations/${selectedLocation.locationCode}/carpooling/${item.id}`
                        : `/carpooling/${item.id}`
                    }
                    className="block bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group overflow-hidden"
                  >
                    <div
                      className={`h-2 w-full ${isDriver ? "bg-blue-500" : "bg-green-500"
                        }`}
                    />
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDriver
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                            }`}
                        >
                          {isDriver ? "OFFERING" : "REQUESTING"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(item.createdAt).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric" },
                          )}
                        </span>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-gray-300 ring-2 ring-gray-100" />
                            <div className="w-0.5 h-6 bg-gray-200" />
                            <div className="w-2 h-2 rounded-full bg-blue-500 ring-2 ring-blue-100" />
                          </div>
                          <div className="flex-1 space-y-3">
                            <div>
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                                Origin
                              </p>
                              <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                                {metadata.origin}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                                Destination
                              </p>
                              <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                                {metadata.destination}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{metadata.time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          {metadata.seats && (
                            <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md">
                              <Users className="w-3 h-3" /> {metadata.seats}
                            </span>
                          )}
                          {metadata.cost && (
                            <span className="font-medium text-gray-900">
                              ${metadata.cost}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
